// src/pages/Reviews.tsx (Updated with error handling)
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Rating,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/api';

interface Review {
  id: number;
  rating: number;
  text: string;
  status: string;
  createdAt: string;
  user?: {
    name: string;
  };
  provider?: {
    businessName: string;
  };
}

interface ServiceProvider {
  id: number;
  businessName: string;
}

const Reviews: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    text: '',
    providerId: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const { user } = useAuth();

  const fetchReviews = async () => {
    try {
      const response = await apiRequest('/reviews');
      if (!response.ok) throw new Error('Failed to fetch reviews');
      const data = await response.json();
      setReviews(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await apiRequest('/providers');
      if (!response.ok) throw new Error('Failed to fetch providers');
      const data = await response.json();
      setProviders(data);
    } catch (err: any) {
      console.error('Failed to fetch providers:', err);
    }
  };

  useEffect(() => {
    fetchReviews();
    fetchProviders();
  }, []);

  const handleSubmitReview = async () => {
    try {
      const response = await apiRequest('/reviews', {
        method: 'POST',
        body: JSON.stringify({
          ...reviewData,
          userId: user?.id,
          status: 'pending'
        }),
      });
      
      if (!response.ok) throw new Error('Failed to submit review');
      
      setMessage({ type: 'success', text: 'Review submitted successfully!' });
      setReviewData({ rating: 5, text: '', providerId: '' });
      setOpenReviewDialog(false);
      fetchReviews();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to submit review' });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Reviews
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setOpenReviewDialog(true)}
        >
          Write a Review
        </Button>
      </Box>
      
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <Grid item xs={12} md={6} key={review.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">
                        {review.provider?.businessName || 'Unknown Provider'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Reviewed by {review.user?.name || 'Anonymous'}
                      </Typography>
                    </Box>
                    <Chip 
                      label={review.status} 
                      color={review.status === 'approved' ? 'success' : review.status === 'pending' ? 'warning' : 'error'}
                      size="small"
                    />
                  </Box>
                  <Rating value={review.rating} readOnly sx={{ mb: 1 }} />
                  <Typography variant="body2">{review.text}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {new Date(review.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Typography>No reviews found.</Typography>
        )}
      </Grid>

      {/* Review Dialog */}
      <Dialog open={openReviewDialog} onClose={() => setOpenReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Write a Review</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Select Service Provider</InputLabel>
            <Select
              value={reviewData.providerId}
              label="Select Service Provider"
              onChange={(e) => setReviewData({ ...reviewData, providerId: e.target.value })}
            >
              {providers.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.businessName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mb: 2 }}>
            <Typography component="legend">Rating</Typography>
            <Rating
              value={reviewData.rating}
              onChange={(event, newValue) => {
                setReviewData({ ...reviewData, rating: newValue || 5 });
              }}
            />
          </Box>
          <TextField
            fullWidth
            label="Review"
            multiline
            rows={4}
            value={reviewData.text}
            onChange={(e) => setReviewData({ ...reviewData, text: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReviewDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitReview} variant="contained">Submit Review</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Reviews;